import type { ProcessBlueprint, ProcessBlueprintVersion } from '../../../types/processBlueprint';

export const BLUEPRINTS_TABLE = 'process_blueprints_with_latest_version';
export const BLUEPRINTS_WRITE_TABLE = 'process_blueprints';

export const BLUEPRINT_WITH_LATEST_VERSION_SELECT = `*`;

const DEFAULT_MAJOR = 1;
const DEFAULT_MINOR = 0;
const DEFAULT_PATCH = 0;

const VERSION_WEIGHT_MAJOR = 10000;
const VERSION_WEIGHT_MINOR = 100;

export function mapVersionRow(row: Record<string, any>): ProcessBlueprintVersion {
  return {
    id: row.id,
    blueprintId: row.blueprint_id,
    organizationId: row.organization_id,
    version: row.version,
    versionSemver: row.version_semver ?? buildSemver(row.version_major, row.version_minor, row.version_patch),
    versionMajor: row.version_major ?? DEFAULT_MAJOR,
    versionMinor: row.version_minor ?? DEFAULT_MINOR,
    versionPatch: row.version_patch ?? DEFAULT_PATCH,
    changeType: row.change_type ?? 'patch',
    snapshotJson: row.snapshot ?? row.snapshot_json ?? {},
    changeSummary: row.change_summary ?? undefined,
    createdBy: row.created_by ?? undefined,
    createdAt: row.created_at,
  };
}

export function mapBlueprintRow(row: Record<string, any>): ProcessBlueprint {
  const latestVersion = extractLatestVersion(row);
  
  const versionMajor = row.version_major ?? latestVersion?.versionMajor ?? DEFAULT_MAJOR;
  const versionMinor = row.version_minor ?? latestVersion?.versionMinor ?? DEFAULT_MINOR;
  const versionPatch = row.version_patch ?? latestVersion?.versionPatch ?? DEFAULT_PATCH;
  const versionSemver = row.version_semver ?? latestVersion?.versionSemver ?? buildSemver(versionMajor, versionMinor, versionPatch);

  return {
    id: row.id,
    organizationId: row.organization_id,
    projectId: row.project_id ?? null,
    name: row.name,
    description: row.description ?? undefined,
    sourceType: row.source_type,
    sourceId: row.source_id ?? null,
    stages: (row.stages ?? []).map((s: any) => ({
      ...s,
      completionType: s.completionType ?? 'simple_complete',
      instruction: s.instruction ?? null,
      exitOptions: s.exitOptions ?? {},
    })),
    generatedTemplateId: row.generated_template_id ?? null,
    generatedMapData: row.generated_map_data ?? null,
    status: row.status,
    versionSemver,
    versionMajor,
    versionMinor,
    versionPatch,
    category: row.category ?? 'geral',
    parentBlueprintId: row.parent_blueprint_id ?? null,
    isCorporateStandard: row.is_corporate_standard ?? (row.status === 'standard'),
    createdBy: row.created_by ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function extractLatestVersion(row: Record<string, any>): ProcessBlueprintVersion | null {
  if (!row?.versions || !Array.isArray(row.versions) || row.versions.length === 0) {
    return null;
  }
  const sorted = [...row.versions].sort((a, b) => {
    const bTime = new Date(b.created_at ?? 0).getTime();
    const aTime = new Date(a.created_at ?? 0).getTime();
    if (bTime !== aTime) return bTime - aTime;
    return scoreVersion(b) - scoreVersion(a);
  });
  return mapVersionRow(sorted[0]);
}

export function scoreVersion(input: { version_major?: number; version_minor?: number; version_patch?: number }): number {
  const major = input.version_major ?? DEFAULT_MAJOR;
  const minor = input.version_minor ?? DEFAULT_MINOR;
  const patch = input.version_patch ?? DEFAULT_PATCH;
  return major * VERSION_WEIGHT_MAJOR + minor * VERSION_WEIGHT_MINOR + patch;
}

export function buildSemver(major?: number | null, minor?: number | null, patch?: number | null): string {
  const safeMajor = Number.isFinite(major) ? Number(major) : DEFAULT_MAJOR;
  const safeMinor = Number.isFinite(minor) ? Number(minor) : DEFAULT_MINOR;
  const safePatch = Number.isFinite(patch) ? Number(patch) : DEFAULT_PATCH;
  return `${safeMajor}.${safeMinor}.${safePatch}`;
}

export function toVersionNumeric(version?: ProcessBlueprintVersion | null): number {
  if (!version) {
    return scoreVersion({ version_major: DEFAULT_MAJOR, version_minor: DEFAULT_MINOR, version_patch: DEFAULT_PATCH });
  }
  return scoreVersion({
    version_major: version.versionMajor,
    version_minor: version.versionMinor,
    version_patch: version.versionPatch,
  });
}
